import { useTranslation } from 'react-i18next';

import { Box, Theme, Tooltip, alpha, lighten, useTheme } from '@mui/material';

import dayjs, { Dayjs } from 'dayjs';

import { barTooltipTitle } from './barTooltip';
import { axisGapDuration } from './bookingsAxis';
import { openOrder } from './openOrder';
import { TimelineBooking } from './timeline.types';
import { getBookingBarColor } from './timelineColors';
import { unavailableStatusLabel } from './unavailability';

interface TimelinePeriodCellProps {
  bookings: TimelineBooking[];
  cellLeft: number;
  cellRight: number;
  /** Bookings-axis break cell: how many days the compressed stretch spans. Renders only the band. */
  axisBreakDays?: number;
  dateToOffset: (date: Dayjs) => number;
  highlightStart?: Dayjs;
  highlightEnd?: Dayjs;
  /** Opt-in per-bar click handler. When absent, a bar with an `orderId` opens that order instead. */
  onBarClick?: (booking: TimelineBooking, event: React.MouseEvent) => void;
}

const BAR_HEIGHT = 10;
const BAR_RADIUS = 9999;

/**
 * How far a booking's colour washes out for a period that is not committed yet — a staged
 * extension or redecoration, or a submitted extension the location has yet to decide on. Light
 * enough that the segment reads as "yours, but not committed", strong enough to cover the bar it
 * may sit on: a staged redecoration paints over the stretch of the booking it re-decorates — the
 * server only splits the bar once the redecoration exists, so the wash is what previews the split.
 */
export const UNCOMMITTED_SEGMENT_WASH = 0.55;

/**
 * The sliver of background between the actual period and the staged one, so the two read as
 * separate pills. Cut with a background-coloured shadow rather than left to the segment gap:
 * the staged segment always sits ON a bar — the extension stretch of the extended own bar, or
 * the re-decorated stretch of the booking — so an ordinary gap would expose that bar's fill,
 * not the background, and the two pills would still touch.
 */
const STAGED_SEGMENT_SEPARATOR = 3;

/**
 * Breathing room at a bar's real ends, so two segments that meet — a conflict against the booking
 * it was cut out of, an availability gap against the booking that closes it — read as two pills
 * rather than one stripe changing colour. Only at real ends: a bar continuing into the next period
 * column must not be nicked at every cell boundary.
 */
const SEGMENT_GAP = 2;

/**
 * How far a bar overhangs the column edge it carries on past.
 *
 * One booking is drawn once per period column it crosses, and below 100% zoom a column edge lands
 * between device pixels: two halves that merely abut there stop touching, and the background shows
 * through as a hairline splitting what is one booking. Overlapping them by a pixel closes it. The
 * halves are the same colour, so the overlap cannot be seen — which is also why it is skipped for
 * translucent bars, where doubling the fill would trade a light seam for a dark one.
 */
const CELL_BLEED = 1;

const CONFLICT_LABEL_COLOR = '#F3786F';

/**
 * The narrowest a bar may draw. A booking whose start and end collapse to the same day has zero
 * geometric length but is still a real booking on a real, labeled segment — invisible is wrong.
 */
const BAR_MIN_WIDTH = 10;

const GAP_BAND_TINT_ALPHA = 0.12;
const GAP_HATCH_STRIPE_ALPHA = 0.45;
const GAP_HATCH_WIDTH = 4;
const GAP_HATCH_STRIPE = 3;
// A vertical seam reads as a column border; a slanted repeating stripe reads as fabric torn at
// this edge — which is the point: the axis was cut here, not merely divided.
const GAP_HATCH_ANGLE = 115;

/**
 * The text a bar writes under itself, if any.
 *
 * Both cases say the same kind of thing — this period is not yours to take — so they share the one
 * label, drawn in the bar's own colour. The status label is a shade darker than the bar it sits
 * under because it is drawn on the cell background, not on the fill.
 */
const barLabel = (
  booking: TimelineBooking,
  theme: Theme,
  t: (key: string) => string,
): { text: string; color: string } | null => {
  if (booking.status === 'CONFLICT') return { text: t('Conflict'), color: CONFLICT_LABEL_COLOR };
  if (booking.unavailableStatus) {
    return {
      text: unavailableStatusLabel(booking.unavailableStatus, t),
      color:
        booking.unavailableStatus === 'OUTSIDE_AVAILABILITY'
          ? theme.palette.grey[600]
          : theme.palette.orange[600],
    };
  }
  return null;
};

const axisBreakLabel = (
  days: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const { unit, count } = axisGapDuration(days);
  return unit === 'months'
    ? t('timeline_axis_break_months', { count })
    : t('timeline_axis_break_days', { count });
};

const TimelinePeriodCell = ({
  bookings,
  cellLeft,
  cellRight,
  axisBreakDays,
  dateToOffset,
  highlightStart,
  highlightEnd,
  onBarClick,
}: TimelinePeriodCellProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // A break column exists only outside the caller's PROTECTED range — buildBookingsAxis never
  // compresses a span that overlaps [protectedStart, protectedEnd] — so on a rail that protects
  // its highlight range (the spaces-page shape, both props fed the same period) discarding
  // bookings and the highlight band here can only ever drop out-of-period synthetic AVAILABLE
  // filler bars. On a rail that highlights WITHOUT protecting (an order/media-plan rail, where
  // the band is the rail's own campaign period), a break can sit inside the band — and replacing
  // the wash with the break's own torn band is exactly the intent: the stretch is compressed,
  // and the band resumes on the sliced columns either side.
  if (axisBreakDays != null && axisBreakDays > 0) {
    const label = axisBreakLabel(axisBreakDays, t);
    const hatch = `repeating-linear-gradient(${GAP_HATCH_ANGLE}deg, ${alpha(theme.palette.grey[400], GAP_HATCH_STRIPE_ALPHA)} 0 ${GAP_HATCH_STRIPE}px, transparent ${GAP_HATCH_STRIPE}px ${GAP_HATCH_STRIPE * 2}px)`;
    return (
      <Tooltip title={label} arrow placement="top">
        <Box
          data-testid="timeline-axis-break"
          aria-label={label}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: alpha(theme.palette.grey[400], GAP_BAND_TINT_ALPHA),
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: GAP_HATCH_WIDTH,
              background: hatch,
            },
            '&::before': { left: 0 },
            '&::after': { right: 0 },
          }}
        />
      </Tooltip>
    );
  }

  const highlightStartX = highlightStart ? dateToOffset(highlightStart.startOf('day')) : -Infinity;
  const highlightEndX = highlightEnd ? dateToOffset(highlightEnd.startOf('day')) : Infinity;
  const hasHighlight =
    (!!highlightStart || !!highlightEnd) && highlightStartX < cellRight && highlightEndX > cellLeft;

  const segments = bookings
    .map((booking) => {
      const hasCampaignPeriod = !!highlightStart;
      if (
        !hasCampaignPeriod &&
        (booking.status === 'AVAILABLE' || booking.status === 'UNAVAILABLE')
      ) {
        return null;
      }

      const startX = dateToOffset(dayjs(booking.start as Date | string).startOf('day'));
      const endDay = dayjs(booking.end as Date | string).startOf('day');
      // A bar that meets the next one is drawn through its end day — see fuseAdjacentBars.
      const rawEndX = dateToOffset(booking.abutsNext ? endDay.add(1, 'day') : endDay);
      const endX = rawEndX > startX ? rawEndX : startX + BAR_MIN_WIDTH;
      if (endX <= cellLeft || startX >= cellRight) return null;
      return {
        booking,
        startX,
        endX,
        startsInCell: startX >= cellLeft && startX < cellRight,
        endsInCell: endX > cellLeft && endX <= cellRight,
      };
    })
    .filter((segment): segment is NonNullable<typeof segment> => segment !== null);

  if (!hasHighlight && segments.length === 0) return <></>;

  const accentMain = theme.palette.accent?.main || '#8E6DE1';

  const bandLeft = highlightStartX > cellLeft ? highlightStartX - cellLeft : 0;
  const bandRight = highlightEndX < cellRight ? cellRight - highlightEndX : 0;
  const bandWidth = Math.max(2, cellRight - cellLeft - bandLeft - bandRight);

  return (
    <>
      {hasHighlight && (
        <Box
          data-testid="timeline-highlight-band"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            // Abut exactly at cell edges (no overhang): the tint is translucent, so any
            // overlap between adjacent cells would double up into darker seams.
            left: bandLeft,
            width: bandWidth,
            backgroundColor: alpha(accentMain, 0.15),
            pointerEvents: 'none',
          }}
        />
      )}
      {segments.map(({ booking, startX, endX, startsInCell, endsInCell }) => {
        const color = getBookingBarColor(booking, theme);
        // Only a bar the backend attached an order to is a link, and only a link gets a pointer.
        const orderId = booking.orderId;
        const bleed = booking.isBackground ? 0 : CELL_BLEED;
        const barLeft = startsInCell ? startX - cellLeft + SEGMENT_GAP : -bleed;
        const barRight = endsInCell ? cellRight - endX + SEGMENT_GAP : -bleed;

        if (booking.isExtension || booking.isStagedRedecoration) {
          // The staged period draws as its own segment in the bar lane, in a washed accent:
          // yours, but not committed yet. Opaque and above its neighbours on purpose — see
          // UNCOMMITTED_SEGMENT_WASH for why it must cover what it overlaps.
          return (
            <Tooltip
              key={booking.id}
              title={t(
                booking.isStagedRedecoration
                  ? 'timeline_redecoration_period'
                  : 'timeline_extension_period',
              )}
              arrow
              placement="top"
            >
              <Box
                data-testid={
                  booking.isStagedRedecoration
                    ? 'timeline-staged-redecoration-indicator'
                    : 'timeline-extension-indicator'
                }
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: barLeft,
                  right: barRight,
                  height: BAR_HEIGHT,
                  transform: 'translateY(-50%)',
                  backgroundColor: lighten(accentMain, UNCOMMITTED_SEGMENT_WASH),
                  boxShadow: startsInCell
                    ? `-${STAGED_SEGMENT_SEPARATOR}px 0 0 0 ${theme.palette.background.paper}`
                    : undefined,
                  borderTopLeftRadius: startsInCell ? BAR_RADIUS : 0,
                  borderBottomLeftRadius: startsInCell ? BAR_RADIUS : 0,
                  borderTopRightRadius: endsInCell ? BAR_RADIUS : 0,
                  borderBottomRightRadius: endsInCell ? BAR_RADIUS : 0,
                  zIndex: 1,
                }}
              />
            </Tooltip>
          );
        }

        if (booking.isPendingExtension) {
          // A submitted extension awaiting the location's decision: same lane and same colour as
          // the booking bar it continues — the client's where the viewer sees one — washed out so
          // it reads as requested rather than owned.
          return (
            <Tooltip key={booking.id} title={t('Extension pending')} arrow placement="top">
              <Box
                data-testid="timeline-pending-extension"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: barLeft,
                  right: barRight,
                  height: BAR_HEIGHT,
                  transform: 'translateY(-50%)',
                  backgroundColor: lighten(color, UNCOMMITTED_SEGMENT_WASH),
                  borderTopLeftRadius: startsInCell ? BAR_RADIUS : 0,
                  borderBottomLeftRadius: startsInCell ? BAR_RADIUS : 0,
                  borderTopRightRadius: endsInCell ? BAR_RADIUS : 0,
                  borderBottomRightRadius: endsInCell ? BAR_RADIUS : 0,
                }}
              />
            </Tooltip>
          );
        }

        const tooltipTitle = barTooltipTitle(booking, t);
        const label = barLabel(booking, theme, t);
        // One label per bar, in the cell its midpoint falls in — a bar crossing four columns is
        // drawn once per column, and each of those pieces would otherwise write the text again.
        const center = (startX + endX) / 2;
        const labelInCell = center >= cellLeft && center < cellRight;

        return (
          <Tooltip key={booking.id} title={tooltipTitle} arrow placement="top">
            <Box
              data-testid={
                booking.isRedecoration
                  ? 'timeline-redecoration'
                  : booking.isExtensionSegment
                    ? 'timeline-extension-segment'
                    : 'timeline-bar'
              }
              data-status={booking.status}
              data-unavailable={booking.unavailableStatus}
              data-own={booking.isOwn ? 'true' : 'false'}
              data-order-id={orderId}
              onClick={
                onBarClick && booking.isClickable
                  ? (event) => onBarClick(booking, event)
                  : orderId
                    ? () => openOrder(orderId)
                    : undefined
              }
              sx={{
                position: 'absolute',
                top: '50%',
                left: barLeft,
                right: barRight,
                height: BAR_HEIGHT,
                transform: 'translateY(-50%)',
                backgroundColor: color,
                borderTopLeftRadius: startsInCell ? BAR_RADIUS : 0,
                borderBottomLeftRadius: startsInCell ? BAR_RADIUS : 0,
                borderTopRightRadius: endsInCell ? BAR_RADIUS : 0,
                borderBottomRightRadius: endsInCell ? BAR_RADIUS : 0,
                opacity: booking.isBackground ? 0.25 : 1,
                cursor: (onBarClick && booking.isClickable) || orderId ? 'pointer' : 'default',
              }}
            >
              {label && labelInCell && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '14px',
                    left: `${center - cellLeft - barLeft}px`,
                    transform: 'translateX(-50%)',
                    // Sized to its text: "Conflict" measured 44px, which the status labels do not.
                    width: 'max-content',
                    height: '15px',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '15px',
                    color: label.color,
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {label.text}
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </>
  );
};

export default TimelinePeriodCell;
